import api from "@/api";
import moment from "moment-timezone";
import { getItemsAndQuantityInCart, updateObjectInArray } from "@/util.js";

let idRequiredDialogDisabled = false;
let _syncGen = 0;
const CART_URL = "/123/v2/cart";
const DELIVERY_URL = "/123/cart/delivery";
const PICKUP_URL = "/123/cart/pickup";
const ITEM_URL = "/123/v2/cart/item";

function formatDeliveryMessage(message) {
  const lastIfIndex = message.lastIndexOf('If');

  if (lastIfIndex > 0) {
    return message.substring(0, lastIfIndex).trim();
  }

  return message;
}

const disableIdRequiredDialogFor20Minutes = () => {
  const twentyMinutesInMilliseconds = 1200000;
  setTimeout(() => {
    idRequiredDialogDisabled = false;
  }, twentyMinutesInMilliseconds);
};

const packageProductModifiers = ({ item_id, modifier_group, qty, upc }) => {
  return {
    ...(item_id && { item_id }),
    modifier_group,
    qty,
    upc,
  };
};

const packageProduct = ({
  item_id,
  modifier_groups,
  modifiers,
  product_session,
  qty,
  upc,
  is_redemption,
  redemption_points_offer_id,
}) => {
  return {
    ...(item_id && { item_id }),
    ...(modifier_groups && {
      is_modifier: true,
      modifiers: modifiers.map(packageProductModifiers),
    }),
    ...(product_session && !item_id && { product_session }),
    qty,
    upc,
    is_redemption,
    redemption_points_offer_id,
  };
};

const state = () => ({
  alcoholOrTobaccoProduct: false,
  awaitingCartUpdate: false,
  awaitingDeliveryDataUpdate: false,
  deliveryDuration: 0,
  deliveryETA: null,
  bringgDeliveryETA: null,
  bringgDeliveryDate: null,
  deliveryErrorMessage: "",
  closedForDeliveryMessage: "",
  dialogs: {
    drawer: false,
    idRequired: false,
  },
  firstOrderDiscount: 0,
  isDeliveryEtaDisplayed: false,
  products: [],
  totalPriceAfterAdjustments: 0,
  unauthenticatedCartButtonClick: false,
});

const actions = {
  closeIdRequiredDialogAndDisable({ commit }) {
    commit("SET_ID_REQUIRED_DIALOG", false);
    idRequiredDialogDisabled = true;
    disableIdRequiredDialogFor20Minutes();
  },

  error({ dispatch }, err) {
    // const networkError = err === "Network Error";
    // networkError && dispatch("cart/fetch", null, { root: true });
    dispatch("error/showError", err, { root: true });
  },

  async fetch({ commit, dispatch }) {
    const gen = ++_syncGen;
    const data = await api({
      method: "GET",
      url: CART_URL,
    });

    if (data.err) return dispatch("cart/error", data.err, { root: true });

    if (_syncGen !== gen) {
      return;
    }

    commit("INIT_CART", data.info);
    dispatch("store/calculateStars", "", { root: true });
  },

  async fetchDeliveryData({ commit, dispatch, rootGetters }) {
    const delivery_address = rootGetters['address/getDeliveryAddress'];
    if (!delivery_address.full_address) {
      return;
    }

    commit('AWAITING_DELIVERY_DATA_UPDATE', true);

    const data = await api({
      data: { delivery_address },
      method: "POST",
      url: DELIVERY_URL,
    });

    commit("AWAITING_DELIVERY_DATA_UPDATE", false);

    if (data.err) {
      commit('CLEAR_BRINGG_DELIVERY_ETA');

      if (data.err.includes('not currently accepting online orders')) {
        commit('orders/UPDATE_SERVICE_TYPE', null, { root: true });
        commit(
          'address/SET_ADDRESS_ERROR',
          {
            status: 'unavailable',
            errorMessage: formatDeliveryMessage(data.err),
          },
          { root: true },
        );

        dispatch('cart/setClosedForDeliveryMessage', data.err, {
          root: true,
        });

        return;
      }

      if (data.err.includes('Delivery Address params are not valid')) {
        commit(
          'address/SET_ADDRESS_ERROR',
          {
            status: 'unknown',
            errorMessage: 'Delivery not available to this address.',
          },
          { root: true },
        );

        return;
      }

      if (data.err.includes('outside our delivery area')) {
        commit(
          'address/SET_ADDRESS_ERROR',
          {
            status: 'outside',
            errorMessage: 'Address is outside our delivery area.',
          },
          { root: true },
        );

        return;
      }

      return dispatch("cart/error", data.err, { root: true });
    }

    if (data.info.error) {
      return dispatch("cart/setDeliveryAddressErrorMessage", data.info.error, {
        root: true,
      });
    }

    if (data.info.delivery_amount) {
      await dispatch(
        "store/update_delivery_amount",
        data.info.delivery_amount,
        {
          root: true,
        },
      );
    }
    if (data.info.delivery_name) {
      await dispatch("store/update_delivery_name", data.info.delivery_name, {
        root: true,
      });
    }
    if (data.info.delivery_days) {
      await dispatch("store/update_delivery_days", data.info.delivery_days, {
        root: true,
      });
    }

    if (data.info.delivery_name === "My Team") {
      commit("SET_MY_TEAM_DELIVERY_DURATION", data.info.delivery_duration || 0);
    } else if (data.info.delivery_name === "Bringg") {
      commit(
        "SET_BRINGG_DELIVERY_ETA",
        data.info.bring_delivery_estimate_local,
      );
    }

    commit("AWAITING_DELIVERY_DATA_UPDATE", false);
  },

  async fetchPickupData({ dispatch }) {
    const data = await api({
      method: "Get",
      url: PICKUP_URL,
    });

    if (data.err) return dispatch("cart/error", data.err, { root: true });

    if (data.info.pickup_days) {
      await dispatch("store/update_pickup_days", data.info.pickup_days, {
        root: true,
      });
    }

    if (data.info.pickup_estimate) {
      await dispatch(
        "store/update_pickup_time_mins",
        data.info.pickup_estimate,
        {
          root: true,
        },
      );
    }
  },

  openIdRequiredDialogIfNotDisabled({ commit }) {
    clearTimeout(disableIdRequiredDialogFor20Minutes);
    commit("SET_ID_REQUIRED_DIALOG", !idRequiredDialogDisabled);
  },
  async removeAllCartItems({ commit, dispatch }, { cartItems, upc }) {
    commit("REMOVE_ALL_CART_ITEMS", upc);

    for (const cartItem of cartItems) {
      await dispatch("cart/remove", cartItem.item_id, {
        root: true,
      });
    }
  },
  async remove({ commit, dispatch }, itemId) {
    _syncGen++;
    commit("AWAITING_CART_UPDATE", true);
    const data = await api({
      data: { item_id: itemId, qty: 0 },
      method: "PUT",
      url: ITEM_URL,
    });
    data.err && dispatch("cart/error", data.err, { root: true });
    commit("AWAITING_CART_UPDATE", false);
  },
  setDeliveryAddressErrorMessage({ commit }, errorMessage) {
    commit("SET_DELIVERY_ADDRESS_ERROR_MESSAGE", errorMessage);
  },
  setClosedForDeliveryMessage({ commit }, errorMessage) {
    commit("SET_CLOSED_FOR_DELIVERY_MESSAGE", errorMessage);
  },
  setIdRequiredDialog({ dispatch }, bool) {
    bool
      ? dispatch("cart/openIdRequiredDialogIfNotDisabled", null, { root: true })
      : dispatch("cart/closeIdRequiredDialogAndDisable", null, { root: true });
  },
  async updateModifierProductFromServerResponse({ commit, dispatch }, product) {
    const method = product.item_id ? "PUT" : "POST";
    const data = await api({
      data: packageProduct(product),
      method,
      url: ITEM_URL,
    });

    data.err
      ? dispatch("cart/error", data.err, { root: true })
      : product.item_id
      ? commit("UPDATE_NOW", product)
      : commit("ADD_MODIFIER_PRODUCT", {
          ...product,
          item_id: data.info.current_product.item_id,
        });
  },
  async updateCartItems({ commit, dispatch }, cartItems) {
    // const start = new Date().getTime();
    for (const cartItem of cartItems) {
      commit("UPDATE_NOW", cartItem);
      await dispatch("cart/updateCart", cartItem, {
        root: true,
      });
    }
  },
  async updateCart({ commit, dispatch }, product) {
    _syncGen++;
    commit("AWAITING_CART_UPDATE", true);
    const method = product.item_id ? "PUT" : "POST";

    const data = await api({
      data: packageProduct(product),
      method,
      url: ITEM_URL,
    });

    const { err, info } = data;

    if (err) {
      dispatch("cart/error", data.err, { root: true });
    } else {
      const added = !product.item_id && info.current_product.item_id;
      added &&
        !commit("ADD_ITEM_ID_BY_PRODUCT_SESSION", {
          itemId: info.current_product.item_id,
          productSession: product.product_session,
        });
    }
    commit("AWAITING_CART_UPDATE", false);
  },
};

const getters = {
  awaitingCartUpdate({ awaitingCartUpdate }) {
    return awaitingCartUpdate;
  },
  containsAlcohol({ products }) {
    return products?.some((_) => _.is_alcohol);
  },
  containsAlcoholOrTobacco({ products }) {
    return products?.some((_) => _.is_alcohol || _.is_tobacco);
  },
  containsTobacco({ products }) {
    return products?.some((_) => _.is_tobacco);
  },
  getProductItemsAndQuantityInCart:
    ({ products }) =>
    (product) => {
      if (!product) {
        return;
      }
      return getItemsAndQuantityInCart(products, product);
    },
  userClickedCartButtonBeforeAuthenticated({
    products,
    unauthenticatedCartButtonClick,
  }) {
    return products.length && unauthenticatedCartButtonClick;
  },
};

const mutations = {
  AWAITING_CART_UPDATE(state, bool) {
    state.awaitingCartUpdate = bool;
  },
  AWAITING_DELIVERY_DATA_UPDATE(state, bool) {
    state.awaitingDeliveryDataUpdate = bool;
  },
  ADD_ITEM_ID_BY_PRODUCT_SESSION(state, { itemId, productSession }) {
    state.products = state.products.map((product) =>
      product.product_session !== productSession
        ? product
        : { ...product, item_id: itemId },
    );
  },
  ADD_MODIFIER_PRODUCT(state, product) {
    state.products = [...state.products, product];
  },
  CLEAR(state) {
    state.products = [];
    state.firstOrderDiscount = 0;
  },
  CLEAR_BRINGG_DELIVERY_ETA(state) {
    state.deliveryErrorMessage = "";
    state.isDeliveryEtaDisplayed = false;
    state.bringgDeliveryETA = null;
  },
  INIT_CART(state, { first_order_discount: firstOrderDiscount, products }) {
    /* you might think you would need only upc_cents here
     * but have news that at the time of this writing
     * upc_cents was not returned form the server unless
     * the quantity was more than 1. if only 1 then upc_cents
     * is not returned but cents is returned
     * (thereby representing the unit cost).
     */
    state.products = products.map((product) => ({
      ...product,
      cents: product.upc_cents,
    }));
    state.firstOrderDiscount = firstOrderDiscount || 0;
  },
  INIT_CART_PRODUCTS(state) {
    state.products = [];
  },
  REMOVE(state, itemId) {
    state.products = state.products.filter(
      (product) => product.item_id !== itemId,
    );
  },
  REMOVE_ALL_CART_ITEMS(state, upc) {
    state.products = state.products.filter((product) => product.upc !== upc);
  },
  SET(state, bool) {
    state.dialogs.drawer = bool;
  },
  SET_BRINGG_DELIVERY_ETA(state, deliveryETA) {
    state.deliveryErrorMessage = "";
    //  TODO keep utc here, and format with moment when updating chosen_time during payment
    state.bringgDeliveryETA = moment(deliveryETA).format("h:mm A");
    state.bringgDeliveryDate = moment(deliveryETA).format("YYYY-MM-DD");
    state.isDeliveryEtaDisplayed = true;
  },
  SET_DELIVERY_ADDRESS_ERROR_MESSAGE(state, errorMessage) {
    state.deliveryErrorMessage = errorMessage;
  },
  SET_CLOSED_FOR_DELIVERY_MESSAGE(state, errorMessage) {
    state.closedForDeliveryMessage = errorMessage;
  },
  SET_ID_REQUIRED_DIALOG(state, bool) {
    state.dialogs.idRequired = bool;
  },
  SET_MY_TEAM_DELIVERY_DURATION(state, deliveryDuration) {
    state.deliveryErrorMessage = "";
    state.deliveryDuration = deliveryDuration || 0;
  },
  SET_TOTAL_PRICE_AFTER_ADJUSTMENTS(state, cents) {
    state.totalPriceAfterAdjustments = cents;
  },
  SET_UNAUTHENTICATED_CART_BUTTON_CLICK(state, bool) {
    state.unauthenticatedCartButtonClick = bool;
  },
  TOGGLE(state) {
    state.dialogs.drawer = !state.dialogs.drawer;
  },
  UPDATE_NOW(state, product) {
    _syncGen++;
    const { item_id, product_session, qty } = product;

    if (qty === 0) {
      state.products = state.products.filter(
        (product) => product.item_id !== item_id,
      );
    } else {
      // if product.pricebook_info.promotions.length then insert new price

      const find = !item_id
        ? { by: "product_session", id: product_session }
        : { by: "item_id", id: item_id };

      const some = state.products.some(
        (product) => product[find.by] === find.id,
      );

      state.products = some
        ? updateObjectInArray(state.products, product, find.by)
        : [...state.products, product];
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
